import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Button,
  Stack,
  Typography,
  Box,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  DialogContent,
  DialogActions,
  Checkbox,
  Grow,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { useSupplier } from '../../hooks/useSupplier';
import { useProduct } from '@features/products';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import ProductSelectBottomSheet from '@features/products/components/selection/ProductSelectBottomSheet';
import { useToast } from '@shared/hooks/useToast';
import { LinkProductRequestDTO } from '@infrastructure/suppliers/dto/SupplierDTO';
import { Product } from '@domain/product/models/Product';
import { Supplier } from '@domain/suppliers/models/Supplier';
import MinimumOrderQuantityMenu from './MinimumOrderQuantityMenu';
import PurchasePriceMenu from './PurchasePriceMenu';
import ProductLinkRow from './ProductLinkRow';
import type { LinkSupplierGlobalFormState, PerProductLinkFormState, LinkSupplierFormState } from './supplierLinkTypes';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import EmptyState from '@shared/ui/common/EmptyState';
import { filterProductsForSupplierLink } from './supplierLinkFormUtils';

export type { LinkSupplierGlobalFormState, PerProductLinkFormState, LinkSupplierFormState } from './supplierLinkTypes';

const defaultGlobalForm: LinkSupplierGlobalFormState = {
  isMain: false,
  currency: 'EUR',
};

const defaultPerProductState: PerProductLinkFormState = {
  purchasePrice: '',
  minimumOrderQuantity: '',
  orderInMultiplesOf: '',
  minimumOrderQuantityType: '',
};

interface LinkProductsToSupplierProps {
  initialProductId?: string;
  initialProduct?: Product;
  initialProducts?: Product[];
  initialSupplier?: Supplier;
  initialFormState?: LinkSupplierFormState;
  initialGlobalForm?: LinkSupplierGlobalFormState;
  initialPerProductForms?: Record<string, PerProductLinkFormState>;
  /** Preserve row selection when reopening from product picker */
  initialSelectedProductIds?: string[];
  onClose: () => void;
}

const LinkProductsToSupplier: React.FC<LinkProductsToSupplierProps> = ({
  initialProductId,
  initialProduct: initialProductProp,
  initialProducts: initialProductsProp,
  initialSupplier,
  initialFormState,
  initialGlobalForm,
  initialPerProductForms,
  initialSelectedProductIds,
  onClose,
}) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const { linkProducts, isLinkingProduct } = useSupplier(initialSupplier?.id ?? '');
  const { error } = useToast();
  const theme = useTheme();
  const { filterCriteria } = useProductsFilter();

  const [moqMenu, setMoqMenu] = useState<{ anchorEl: HTMLElement | null; productId: string | null }>({
    anchorEl: null,
    productId: null,
  });

  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    () => new Set(initialSelectedProductIds ?? []),
  );
  const [batchMoqAnchor, setBatchMoqAnchor] = useState<null | HTMLElement>(null);
  const [batchMoqDraft, setBatchMoqDraft] = useState<PerProductLinkFormState>({ ...defaultPerProductState });
  const [batchPurchasePriceAnchor, setBatchPurchasePriceAnchor] = useState<null | HTMLElement>(null);
  const [batchPurchasePriceDraft, setBatchPurchasePriceDraft] = useState<PerProductLinkFormState>({ ...defaultPerProductState });
  const [moqMenuDraft, setMoqMenuDraft] = useState<PerProductLinkFormState>({ ...defaultPerProductState });

  const { product: productFromId } = useProduct(initialProductId);
  const resolvedInitialProduct = initialProductProp ?? (initialProductId && productFromId ? productFromId : undefined);

  const [products, setProducts] = useState<Product[]>(() => {
    if (initialProductsProp && initialProductsProp.length > 0) return initialProductsProp;
    if (resolvedInitialProduct) return [resolvedInitialProduct];
    return [];
  });
  const [supplier, setSupplier] = useState<Supplier | undefined>(initialSupplier);
  const [globalForm, setGlobalForm] = useState<LinkSupplierGlobalFormState>(() => ({
    ...defaultGlobalForm,
    ...initialGlobalForm,
  }));
  const [perProductForms, setPerProductForms] = useState<Record<string, PerProductLinkFormState>>(() => ({
    ...initialPerProductForms,
  }));

  useEffect(() => {
    if (initialProductsProp && initialProductsProp.length > 0) setProducts(initialProductsProp);
    else if (initialProductProp) setProducts([initialProductProp]);
    else if (initialProductId && productFromId) setProducts([productFromId]);
  }, [initialProductsProp?.length, initialProductProp?.id, initialProductId, productFromId?.id]);

  useEffect(() => {
    if (initialSupplier) setSupplier(initialSupplier);
  }, [initialSupplier?.id]);

  const legacyFormAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialFormState || legacyFormAppliedRef.current || products.length < 1) return;
    legacyFormAppliedRef.current = true;
    setGlobalForm(prev => ({
      isMain: initialFormState.isMain ?? prev.isMain,
      currency: (initialFormState.currency || prev.currency || 'EUR').trim() || 'EUR',
    }));
    const firstId = products[0].id;
    setPerProductForms(prev => ({
      ...prev,
      [firstId]: {
        purchasePrice: initialFormState.purchasePrice ?? prev[firstId]?.purchasePrice ?? '',
        minimumOrderQuantity: initialFormState.minimumOrderQuantity ?? prev[firstId]?.minimumOrderQuantity ?? '',
        orderInMultiplesOf: initialFormState.orderInMultiplesOf ?? prev[firstId]?.orderInMultiplesOf ?? '',
        minimumOrderQuantityType:
          initialFormState.minimumOrderQuantityType ?? prev[firstId]?.minimumOrderQuantityType ?? '',
      },
    }));
  }, [initialFormState, products]);

  useEffect(() => {
    setPerProductForms(prev => {
      const next = { ...prev };
      for (const p of products) {
        if (!next[p.id]) {
          next[p.id] = { ...defaultPerProductState };
        }
      }
      for (const key of Object.keys(next)) {
        if (!products.some(pr => pr.id === key)) {
          delete next[key];
        }
      }
      return next;
    });
  }, [products]);

  useEffect(() => {
    const allowed = new Set(products.map(p => p.id));
    setSelectedProductIds(prev => new Set([...prev].filter(id => allowed.has(id))));
  }, [products]);

  const filteredProducts = useMemo(
    () => filterProductsForSupplierLink(products, filterCriteria),
    [products, filterCriteria],
  );

  const filteredIds = useMemo(() => filteredProducts.map(p => p.id), [filteredProducts]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every(id => selectedProductIds.has(id));
  const someFilteredSelected =
    filteredIds.some(id => selectedProductIds.has(id)) && !allFilteredSelected;

  const toggleSelectFiltered = () => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach(id => next.delete(id));
      } else {
        filteredIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const toggleSelectOne = (productId: string) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const openBatchMoqMenu = (e: React.MouseEvent<HTMLElement>) => {
    const ids = [...selectedProductIds];
    const first = ids[0];
    setBatchMoqDraft(
      first ? { ...(perProductForms[first] ?? defaultPerProductState) } : { ...defaultPerProductState },
    );
    setBatchMoqAnchor(e.currentTarget);
  };

  const openBatchPurchasePriceMenu = (e: React.MouseEvent<HTMLElement>) => {
    const ids = [...selectedProductIds];
    const first = ids[0];
    setBatchPurchasePriceDraft(
      first ? { ...(perProductForms[first] ?? defaultPerProductState) } : { ...defaultPerProductState },
    );
    setBatchPurchasePriceAnchor(e.currentTarget);
  };

  const closeBatchPurchasePriceMenu = () => {
    setBatchPurchasePriceAnchor(null);
  };

  const closeBatchMoqMenu = () => {
    setBatchMoqAnchor(null);
  };

  const applyBatchMoqToSelection = () => {
    if (selectedProductIds.size === 0) return;
    const patch = { ...batchMoqDraft };
    setPerProductForms(prev => {
      const next = { ...prev };
      for (const id of selectedProductIds) {
        next[id] = { ...(next[id] ?? defaultPerProductState), ...patch };
      }
      return next;
    });
  };

  const applyBatchPurchasePriceToSelection = () => {
    if (selectedProductIds.size === 0) return;
    const purchasePrice = batchPurchasePriceDraft.purchasePrice;
    setPerProductForms(prev => {
      const next = { ...prev };
      for (const id of selectedProductIds) {
        next[id] = { ...(next[id] ?? defaultPerProductState), purchasePrice };
      }
      return next;
    });
  };

  const updateRow = useCallback((productId: string, patch: Partial<PerProductLinkFormState>) => {
    setPerProductForms(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] ?? defaultPerProductState), ...patch },
    }));
  }, []);

  const buildRestore = () => ({
    products,
    supplier,
    globalForm,
    perProductForms,
    selectedProductIds: [...selectedProductIds],
  });

  const handleOpenSelectProduct = () => {
    const stateToRestore = buildRestore();
    const linkedIds = new Set(supplier?.supplierProducts?.map(lp => lp.product?.id).filter(Boolean) ?? []);
    openBottomSheet({
      title: 'Select products',
      component: (
        <ProductSelectBottomSheet
          initialProducts={products}
          excludeProductIds={new Set(Array.from(linkedIds).filter(Boolean) as string[])}
          getDisabledCaption={() => 'Already linked'}
          onFinish={selections => {
            const selected = selections.map(s => s.product);
            closeBottomSheet();
            openBottomSheet({
              title: 'Add Supplier to Product',
              component: (
                <LinkProductsToSupplier
                  initialProducts={selected}
                  initialSupplier={stateToRestore.supplier}
                  initialGlobalForm={stateToRestore.globalForm}
                  initialPerProductForms={stateToRestore.perProductForms}
                  onClose={onClose}
                />
              ),
              size: BottomSheetSize.MD,
            });
          }}
          onClose={() => {
            closeBottomSheet();
            openBottomSheet({
              title: 'Add Supplier to Product',
              component: (
                <LinkProductsToSupplier
                  initialProducts={stateToRestore.products}
                  initialSupplier={stateToRestore.supplier}
                  initialGlobalForm={stateToRestore.globalForm}
                  initialPerProductForms={stateToRestore.perProductForms}
                  initialSelectedProductIds={stateToRestore.selectedProductIds}
                  onClose={onClose}
                />
              ),
              size: BottomSheetSize.MD,
            });
          }}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const buildOptionsForProduct = (productId: string): Partial<LinkProductRequestDTO> | undefined => {
    const row = perProductForms[productId] ?? defaultPerProductState;
    const opts: Partial<LinkProductRequestDTO> = {};
    if (globalForm.isMain) opts.isMain = true;
    const curr = (globalForm.currency || 'EUR').trim();
    if (curr) opts.currency = curr;
    const price = row.purchasePrice.trim();
    if (price) opts.purchasePrice = parseFloat(price.replace(',', '.'));
    const minQty = row.minimumOrderQuantity.trim();
    if (minQty) {
      const parsed = parseFloat(minQty.replace(',', '.'));
      if (Number.isFinite(parsed)) {
        const t = row.minimumOrderQuantityType || 'items';
        opts.minimumOrderQuantity = t === 'items' ? Math.floor(Math.abs(parsed)) : parsed;
        opts.minimumOrderQuantityType = t;
      }
    }
    const multOf = row.orderInMultiplesOf.trim();
    if (multOf) opts.orderInMultiplesOf = parseFloat(multOf.replace(',', '.'));
    return Object.keys(opts).length > 0 ? opts : undefined;
  };

  const handleSubmit = async () => {
    if (products.length === 0 || !supplier?.id) return;

    try {

      await linkProducts({
        supplierId: supplier.id,
        products: products.map(product => ({
          productId: product.id,
          ...buildOptionsForProduct(product.id),
        })),
      });

      onClose();
    } catch {
      error('Failed to link supplier');
    }
  };

  const handleClose = () => {
    setProducts([]);
    setSupplier(undefined);
    setGlobalForm(defaultGlobalForm);
    setPerProductForms({});
    setSelectedProductIds(new Set());
    setBatchMoqAnchor(null);
    setBatchPurchasePriceAnchor(null);
    setMoqMenu({ anchorEl: null, productId: null });
    onClose();
  };

  const cc = globalForm.currency || 'EUR';

  const openMoqMenu = (e: React.MouseEvent<HTMLElement>, productId: string) => {
    setMoqMenuDraft({ ...(perProductForms[productId] ?? defaultPerProductState) });
    setMoqMenu({ anchorEl: e.currentTarget, productId });
  };

  const closeMoqMenu = () => {
    setMoqMenu({ anchorEl: null, productId: null });
  };

  const confirmMoqMenu = () => {
    if (moqMenu.productId) {
      updateRow(moqMenu.productId, {
        minimumOrderQuantity: moqMenuDraft.minimumOrderQuantity,
        minimumOrderQuantityType: moqMenuDraft.minimumOrderQuantityType,
        orderInMultiplesOf: moqMenuDraft.orderInMultiplesOf,
      });
    }
    closeMoqMenu();
  };

  const moqRow = moqMenu.productId ? moqMenuDraft : defaultPerProductState;

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6" fontWeight={600}>
            Link products to supplier
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={handleClose}
            sx={{ display: 'flex', p: 0.5, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            <Close />
          </Box>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 0 }}>
        <Stack sx={{ p: 0 }} spacing={0}>
          {products.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Box
                component="button"
                type="button"
                onClick={handleOpenSelectProduct}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <Avatar variant="rounded" sx={{ width: 48, height: 48, border: 1, borderColor: 'divider' }}>
                  ?
                </Avatar>
                <Typography variant="body1" color="primary">
                  Select products to link
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Stack
                spacing={1.5}
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  bgcolor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1.5}
                >
                  <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} flex={1} minWidth={0}>
                    <ProductsFilter />
                    <ActiveFilters />
                  </Stack>
                  <Grow in={selectedProductIds.size > 0}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Edit {selectedProductIds.size} product{selectedProductIds.size !== 1 ? 's' : ''}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{
                          pointerEvents: 'auto',
                          color: 'white',
                          backgroundColor: theme.palette.secondary.main,
                          p: 0.5,
                          borderRadius: 2,
                          boxShadow: `0px 0px 16px 0px ${theme.palette.primary.main}33`,
                        }}
                      >
                        <Button
                          variant="text"
                          color="inherit"
                          onClick={openBatchMoqMenu}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          MOQ
                        </Button>
                        <Button
                          variant="text"
                          color="inherit"
                          onClick={openBatchPurchasePriceMenu}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Purchase price
                        </Button>
                      </Stack>
                    </Stack>
                  </Grow>
                </Stack>
              </Stack>

              {filteredProducts.length === 0 ? (
                <Box sx={{ px: 2, py: 4 }}>
                  <EmptyState
                    title="No products match filters"
                    description="Adjust filters or clear them to see products in this list."
                  />
                </Box>
              ) : (
                <TableContainer sx={{ px: 0 }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                        <TableCell padding="checkbox" sx={{ pl: 1 }}>
                          <Checkbox
                            indeterminate={someFilteredSelected}
                            checked={allFilteredSelected}
                            onChange={toggleSelectFiltered}
                            inputProps={{ 'aria-label': 'Select all filtered products' }}
                          />
                        </TableCell>
                        <TableCell sx={{ p: 2 }}>Product</TableCell>
                        <TableCell align="right" sx={{ p: 2 }}>
                          MOQ
                        </TableCell>
                        <TableCell align="right" sx={{ p: 2, minWidth: 140 }}>
                          Purchase price
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredProducts.map(p => (
                        <ProductLinkRow
                          key={p.id}
                          product={p}
                          row={perProductForms[p.id] ?? defaultPerProductState}
                          currencyCode={cc}
                          onOpenMoqMenu={e => openMoqMenu(e, p.id)}
                          onPurchasePriceChange={value => updateRow(p.id, { purchasePrice: value })}
                          selected={selectedProductIds.has(p.id)}
                          onToggleSelect={() => toggleSelectOne(p.id)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Stack>

        <MinimumOrderQuantityMenu
          anchorEl={moqMenu.anchorEl}
          open={Boolean(moqMenu.anchorEl && moqMenu.productId)}
          onClose={closeMoqMenu}
          onConfirm={confirmMoqMenu}
          currencyCode={cc}
          minimumOrderQuantity={moqRow.minimumOrderQuantity}
          minimumOrderQuantityType={moqRow.minimumOrderQuantityType}
          orderInMultiplesOf={moqRow.orderInMultiplesOf}
          onChange={patch => setMoqMenuDraft(prev => ({ ...prev, ...patch }))}
        />

        <MinimumOrderQuantityMenu
          anchorEl={batchMoqAnchor}
          open={Boolean(batchMoqAnchor)}
          onClose={closeBatchMoqMenu}
          onConfirm={() => {
            applyBatchMoqToSelection();
            closeBatchMoqMenu();
          }}
          currencyCode={cc}
          minimumOrderQuantity={batchMoqDraft.minimumOrderQuantity}
          minimumOrderQuantityType={batchMoqDraft.minimumOrderQuantityType}
          orderInMultiplesOf={batchMoqDraft.orderInMultiplesOf}
          onChange={patch => setBatchMoqDraft(prev => ({ ...prev, ...patch }))}
        />

        <PurchasePriceMenu
          anchorEl={batchPurchasePriceAnchor}
          open={Boolean(batchPurchasePriceAnchor)}
          onClose={closeBatchPurchasePriceMenu}
          onConfirm={() => {
            applyBatchPurchasePriceToSelection();
            closeBatchPurchasePriceMenu();
          }}
          currencyCode={cc}
          purchasePrice={batchPurchasePriceDraft.purchasePrice}
          onChange={value => setBatchPurchasePriceDraft(prev => ({ ...prev, purchasePrice: value }))}
        />
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 3, justifyContent: 'flex-end' }}>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          startIcon={<Add />}
          disabled={products.length === 0 || !supplier?.id || isLinkingProduct}
        >
          {isLinkingProduct
            ? 'Linking...'
            : products.length > 1
              ? `Add supplier to ${products.length} products`
              : 'Add supplier'}
        </Button>
      </DialogActions>
    </>
  );
};

export default LinkProductsToSupplier;
